import React from 'react';

import { useAuthentication } from '@context/AuthContext';
import { listenToPendingFollowUserRequestUserIds } from '@db/Requests/FollowUser';
import { listenToPendingJoinGroupRequestGroupIds } from '@db/Requests/JoinGroup';
import { listenToPendingJoinPlanRequestPlanIds } from '@db/Requests/JoinPlan';
import { createNamedContext, useRequiredContext } from '@helpers/Context';
import { logError } from '@helpers/Loggers';

export type RequestsContextProps = {
  pendingFollowUserRequestUserIds: Set<string>;
  pendingJoinGroupRequestGroupIds: Set<string>;
  pendingJoinPlanRequestPlanIds: Set<string>;
};

export const RequestsContext = createNamedContext<RequestsContextProps>({
  RequestsContext: undefined,
});

export function useRequests() {
  return useRequiredContext(RequestsContext);
}

export function RequestsProvider({ children }: React.PropsWithChildren) {
  const { loggedIn } = useAuthentication();
  const [pendingFollowUserRequestUserIds, setPendingFollowUserRequestUserIds] = React.useState<
    Set<string>
  >(new Set());
  const [pendingJoinGroupRequestGroupIds, setPendingJoinGroupRequestGroupIds] = React.useState<
    Set<string>
  >(new Set());
  const [pendingJoinPlanRequestPlanIds, setPendingJoinPlanRequestPlanIds] = React.useState<
    Set<string>
  >(new Set());

  React.useEffect(() => {
    if (loggedIn)
      return listenToPendingFollowUserRequestUserIds(
        (userIds) => setPendingFollowUserRequestUserIds(userIds),
        (error) => logError(error)
      );
  }, [loggedIn]);

  React.useEffect(() => {
    if (loggedIn)
      return listenToPendingJoinPlanRequestPlanIds(
        (planIds) => setPendingJoinPlanRequestPlanIds(planIds),
        (error) => logError(error)
      );
  }, [loggedIn]);

  React.useEffect(() => {
    if (loggedIn)
      return listenToPendingJoinGroupRequestGroupIds(
        (groupIds) => setPendingJoinGroupRequestGroupIds(groupIds),
        (error) => logError(error)
      );
  }, [loggedIn]);

  const value = {
    pendingFollowUserRequestUserIds,
    pendingJoinGroupRequestGroupIds,
    pendingJoinPlanRequestPlanIds,
  };

  return <RequestsContext.Provider value={value}>{children}</RequestsContext.Provider>;
}
